import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
          .then(async ({ detail }) => {
      const payload = event.detail || {};

      // Normal flow: a variant resolved for the full selection, so swap in
      // the server-rendered gallery for it (stock behavior).
      if (detail?.html && payload.variantId) {
        const newMediaGallery = detail.html.querySelector('media-gallery');
        if (newMediaGallery) {
          this.replaceWith(newMediaGallery);
          return;
        }
      }

      // Fallback: no variant resolved (e.g. that size doesn't exist in the
      // chosen color). Re-request the product page with ONLY the option value
      // that was just clicked (usually the color) and use that gallery.
      // Display-only: price, SKU, and the sold-out button are untouched.
      const optionValueId = payload.optionValueId;
      if (optionValueId) {
        try {
          const url = new URL(window.location.pathname, window.location.origin);
          url.searchParams.set('option_values', optionValueId);
          const view = new URLSearchParams(window.location.search).get('view');
          if (view) url.searchParams.set('view', view);

          const response = await fetch(url.toString());
          if (response.ok) {
            const text = await response.text();
            const html = new DOMParser().parseFromString(text, 'text/html');
            const fallbackGallery = html.querySelector('media-gallery');
            if (fallbackGallery) {
              this.replaceWith(fallbackGallery);
              return;
            }
          }
        } catch {
          // Best effort only; fall through to stock behavior below.
        }
      }

      // Last resort, identical to stock behavior: swap in whatever HTML the
      // picker fetched, or do nothing.
      if (detail?.html) {
        const newMediaGallery = detail.html.querySelector('media-gallery');
        if (newMediaGallery) this.replaceWith(newMediaGallery);
      }
    })
    // Fallback: when the selected option combination doesn't resolve to a
      // variant (e.g. the chosen size doesn't exist in the chosen color), the
      // server sends no updated gallery. Re-fetch just this section using ONLY
      // the option value that was just picked (usually the color) and swap in
      // that gallery instead. Display-only: price, SKU, and the sold-out
    
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
